import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, login, logout, refreshSession } from './api';
import { clearAuthState, loadAuthState, saveAuthState } from './auth-store';

export function useAuthSession() {
  const queryClient = useQueryClient();

  const state = loadAuthState();

  const loginMutation = useMutation({
    mutationFn: async (input: { email: string; password: string }) => login(input.email, input.password),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => logout(),
    onSettled: () => {
      clearAuthState();
      queryClient.clear();
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async () => refreshSession(),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  return {
    state,
    loginMutation,
    logoutMutation,
    refreshMutation,
  };
}

export function useSignupTenant() {
  return useMutation({
    mutationFn: async (payload: {
      tenantName: string;
      tenantSlug: string;
      name: string;
      email: string;
      password: string;
    }) => {
      return apiRequest<{ tenantId: string; userId: string; message: string }>('/v1/auth/signup', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}

export function useStartSignupCheckout() {
  return useMutation({
    mutationFn: async (payload: {
      tenantName: string;
      tenantSlug: string;
      name: string;
      email: string;
      password: string;
      successUrl: string;
      cancelUrl: string;
    }) => {
      return apiRequest<{ id: string; url?: string }>('/v1/auth/signup/checkout-session', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}

export function useCompleteSignupCheckout() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const payload = await apiRequest<{ accessToken: string; user: any; message: string }>('/v1/auth/signup/complete', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      });

      const previous = loadAuthState();
      saveAuthState({
        accessToken: payload.accessToken,
        user: payload.user,
        activeTenantId: previous.activeTenantId || payload.user.memberships?.[0]?.tenantId || null,
      });

      return payload;
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      return apiRequest<{ ok: boolean; message: string }>('/v1/auth/password/forgot', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (payload: { token: string; password: string }) => {
      return apiRequest<{ ok: boolean; message: string }>('/v1/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}

export function useCurrentUser(options?: { enabled?: boolean }) {
  const hasAccessToken = Boolean(loadAuthState().accessToken);
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiRequest<{ user: any }>('/v1/auth/me').then((payload) => payload.user),
    retry: false,
    enabled: hasAccessToken && (options?.enabled ?? true),
  });
}

export function useTenantCurrent(options?: { enabled?: boolean }) {
  const hasAccessToken = Boolean(loadAuthState().accessToken);
  return useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: () => apiRequest<{ tenant: any }>('/v1/tenants/current').then((payload) => payload.tenant),
    enabled: hasAccessToken && (options?.enabled ?? true),
  });
}

export function useUpdateBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return apiRequest<{ branding: any }>('/v1/tenants/current/branding', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', 'current'] });
    },
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['tenant', 'flags'],
    queryFn: () => apiRequest<{ flags: any[] }>('/v1/tenants/current/feature-flags').then((payload) => payload.flags),
  });
}

export function useSetFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { key: string; value: unknown }) => {
      return apiRequest<{ flag: any }>(`/v1/tenants/current/feature-flags/${input.key}`, {
        method: 'PUT',
        body: JSON.stringify({
          value: input.value,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', 'flags'] });
    },
  });
}

export function useLocations(filters?: { status?: string; search?: string; page?: number; pageSize?: number }) {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));

  const suffix = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: ['locations', filters],
    queryFn: () => apiRequest<{ rows: any[]; total: number }>(`/v1/locations${suffix}`),
  });
}

export function useLocation(locationRef: string | null) {
  return useQuery({
    queryKey: ['location', locationRef],
    queryFn: () => apiRequest<{ location: any }>(`/v1/locations/${locationRef}`).then((payload) => payload.location),
    enabled: Boolean(locationRef),
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return apiRequest<{ location: any }>('/v1/locations', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export function useUpdateLocation(locationRef: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return apiRequest<{ location: any }>(`/v1/locations/${locationRef}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['location', locationRef] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationRef: string) => {
      return apiRequest(`/v1/locations/${locationRef}`, {
        method: 'DELETE',
      });
    },
    onSuccess: (_, locationRef) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['location', locationRef] });
      queryClient.invalidateQueries({ queryKey: ['brief', locationRef] });
      queryClient.invalidateQueries({ queryKey: ['location-posts', locationRef] });
    },
  });
}

export function useLocationBrief(locationRef: string | null) {
  return useQuery({
    queryKey: ['brief', locationRef],
    queryFn: () => apiRequest<{ brief: any }>(`/v1/locations/${locationRef}/brief`).then((payload) => payload.brief),
    enabled: Boolean(locationRef),
  });
}

export function useUpdateLocationBrief(locationRef: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return apiRequest<{ brief: any }>(`/v1/locations/${locationRef}/brief`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brief', locationRef] });
    },
  });
}

export function useLocationPosts(locationRef: string | null) {
  return useQuery({
    queryKey: ['location-posts', locationRef],
    queryFn: () => apiRequest<{ posts: any[] }>(`/v1/locations/${locationRef}/posts`).then((payload) => payload.posts),
    enabled: Boolean(locationRef),
  });
}

export function useRunLocation(locationRef: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload?: { reason?: string; force?: boolean }) => {
      return apiRequest<{ runId: string; message: string }>(`/v1/locations/${locationRef}/run`, {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] });
      queryClient.invalidateQueries({ queryKey: ['location-posts', locationRef] });
    },
  });
}

export function useRuns(filters?: {
  locationId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  const params = new URLSearchParams();
  if (filters?.locationId) params.set('locationId', filters.locationId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.set('dateTo', filters.dateTo);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));

  const suffix = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: ['runs', filters],
    queryFn: () => apiRequest<{ rows: any[]; total: number }>(`/v1/runs${suffix}`),
    enabled: filters?.enabled ?? true,
  });
}

export function useRunDetail(runId: string | null) {
  return useQuery({
    queryKey: ['run', runId],
    queryFn: () => apiRequest<{ run: any }>(`/v1/runs/${runId}`).then((payload) => payload.run),
    enabled: Boolean(runId),
  });
}

export function useRetryRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runId: string) => {
      return apiRequest<{ runId: string }>(`/v1/runs/${runId}/retry`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
  });
}

export function useSchedulerScan() {
  return useMutation({
    mutationFn: async () => {
      return apiRequest<{ message: string; jobId: string }>('/v1/runs/scheduler/scan', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
  });
}

export function useAnalyticsOverview(
  range: '7d' | '30d' | '90d' | '365d',
  locationId?: string,
  options?: { enabled?: boolean },
) {
  const params = new URLSearchParams({ range });
  if (locationId) params.set('locationId', locationId);

  return useQuery({
    queryKey: ['analytics-overview', range, locationId],
    queryFn: () => apiRequest(`/v1/analytics/overview?${params.toString()}`),
    enabled: options?.enabled ?? true,
  });
}

export function useAnalyticsLocations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['analytics-locations'],
    queryFn: () => apiRequest<{ rows: any[] }>('/v1/analytics/locations').then((payload) => payload.rows),
    enabled: options?.enabled ?? true,
  });
}

export function useOnboardingGuide() {
  return useQuery({
    queryKey: ['onboarding-guide'],
    queryFn: () => apiRequest('/v1/onboarding/guide'),
  });
}

export function useGoogleConnections() {
  return useQuery({
    queryKey: ['google-connections'],
    queryFn: () => apiRequest<{ connections: any[] }>('/v1/onboarding/google/connections').then((payload) => payload.connections),
  });
}

export function useGoogleOAuthConfig() {
  return useQuery({
    queryKey: ['google-oauth-config'],
    queryFn: () =>
      apiRequest<{
        configured: boolean;
        source: 'TENANT' | 'ENV' | 'NONE';
        redirectUri?: string;
        clientIdMasked?: string;
        updatedAt?: string;
      }>('/v1/onboarding/google/oauth-config'),
  });
}

export function useSaveGoogleOAuthConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { clientId: string; clientSecret: string; redirectUri?: string }) => {
      return apiRequest<{
        ok: boolean;
        configured: boolean;
        source: 'TENANT';
        redirectUri: string;
        clientIdMasked: string;
      }>('/v1/onboarding/google/oauth-config', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-oauth-config'] });
    },
  });
}

export function useGoogleConnectUrl() {
  return useMutation({
    mutationFn: async () => apiRequest<{ url: string; state: string }>('/v1/onboarding/google/connect-url', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  });
}

export function useGoogleConnectionPing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      return apiRequest(`/v1/onboarding/google/connections/${connectionId}/ping`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-connections'] });
    },
  });
}

export function useImportGoogleLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload?: { connectionId?: string }) => {
      return apiRequest<{
        ok: boolean;
        summary: {
          created: number;
          updated: number;
          skipped: number;
          importedLocationIds: string[];
        };
        results: Array<{
          connectionId: string;
          created: number;
          updated: number;
          skipped: number;
          importedLocationIds: string[];
        }>;
      }>('/v1/onboarding/google/import-locations', {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-locations'] });
    },
  });
}

export function useBillingSubscription() {
  return useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => apiRequest<{ subscription: any }>('/v1/billing/subscription').then((payload) => payload.subscription),
  });
}

export function useBillingInvoices() {
  return useQuery({
    queryKey: ['billing-invoices'],
    queryFn: () => apiRequest<{ invoices: any[] }>('/v1/billing/invoices').then((payload) => payload.invoices),
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (payload: { priceId: string; successUrl: string; cancelUrl: string }) => {
      return apiRequest<{ url?: string; id: string }>('/v1/billing/checkout-session', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}

export function useAuditLogs(page = 1, pageSize = 25) {
  return useQuery({
    queryKey: ['audit-logs', page, pageSize],
    queryFn: () => apiRequest<{ rows: any[]; total: number }>(`/v1/audit/logs?page=${page}&pageSize=${pageSize}`),
  });
}

export function useSecurityEvents(page = 1, pageSize = 25) {
  return useQuery({
    queryKey: ['security-events', page, pageSize],
    queryFn: () =>
      apiRequest<{ rows: any[]; total: number }>(`/v1/audit/security-events?page=${page}&pageSize=${pageSize}`),
  });
}

export function useTenantUsers() {
  return useQuery({
    queryKey: ['tenant-users'],
    queryFn: () => apiRequest<{ memberships: any[] }>('/v1/users').then((payload) => payload.memberships),
  });
}

export function useCreateTenantUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      email: string;
      name: string;
      password: string;
      role: 'ADMIN' | 'AGENCY' | 'AGENCY_DEMO' | 'LOCATION_USER';
    }) => {
      return apiRequest('/v1/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
    },
  });
}

export function switchActiveTenant(tenantId: string) {
  const state = loadAuthState();
  saveAuthState({
    ...state,
    activeTenantId: tenantId,
  });
}

export function useAdminTenants() {
  return useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => apiRequest<{ tenants: any[] }>('/v1/admin/tenants').then((payload) => payload.tenants),
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiRequest<{ users: any[] }>('/v1/admin/users').then((payload) => payload.users),
  });
}

export function useAdminAuditLogs() {
  return useQuery({
    queryKey: ['admin-audit-logs'],
    queryFn: () =>
      apiRequest<{ logs: any[] }>('/v1/admin/audit-logs').then((payload) => payload.logs),
  });
}

export function useAdminInviteUser() {
  return useMutation({
    mutationFn: async (payload: {
      tenantId: string;
      email: string;
      role: 'ADMIN' | 'AGENCY' | 'AGENCY_DEMO' | 'LOCATION_USER';
      expiresInDays?: number;
    }) => {
      return apiRequest('/v1/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });
}
